import { isABSOrADSSearch } from 'utils/booking';
import { applicableCarriers } from 'constants/timetableServices';

import { serviceStopStatuses, extendedServiceStopStatuses } from 'types/timetableServices';
import { languages, locales } from 'types/language';

/**
 * A timetable service. This is the object as it comes from the API.
 *
 * @typedef {Object} TimetableService
 * @property {Object} model - The model object.
 * @property {string} model.carrier
 * @property {string} model.trainNumber
 * @property {TimetableServiceStatus} model.serviceStatus
 * @property {TimetableServiceStop[]} serviceStops
 */

/**
 * A service stop in a timetable service. This is the object as it comes from the API.
 *
 * @typedef {Object} TimetableServiceStop
 * @property {Object} model - The model object.
 * @property {ServiceStopStatus} model.arrivalStatus
 * @property {ServiceStopStatus} model.departureStatus
 * @property {number} model.stopNumber
 * @property {string|null} model.actualArrivalDateTime
 * @property {string|null} model.actualDepartureDateTime
 * @property {string|null} model.commercialArrivalDateTime
 * @property {string|null} model.commercialDepartureDateTime
 * @property {string|null} model.expectedArrivalDateTime
 * @property {string|null} model.expectedDepartureDateTime
 * @property {Object} station - The station object.
 * @property {string} station.uic
 * @property {string} station.shortCode
 * @property {Object} station.name - The name object.
 * @property {string} station.name.en
 * @property {string} station.name.fr
 * @property {string} station.name.de
 * @property {string} station.name.nl
 */

/**
 * A parsed TimetableServiceStop model
 *
 * @typedef {Object} ServiceStopModel
 * @property {ServiceStopStatus} arrivalStatus
 * @property {ServiceStopStatus} departureStatus
 * @property {number} stopNumber
 * @property {Date|null} actualArrivalDateTime
 * @property {Date|null} actualDepartureDateTime
 * @property {Date|null} commercialArrivalDateTime
 * @property {Date|null} commercialDepartureDateTime
 * @property {Date|null} expectedArrivalDateTime
 * @property {Date|null} expectedDepartureDateTime
 */

/**
 * @typedef {('CANCELLED' | 'LIMITED_SERVICE' | 'SEATING_CHANGED' | 'UNAFFECTED')} TimetableServiceStatus
 */

/**
 * @typedef {('ARRIVED' | 'DEPARTED' | 'ON_TIME' | 'EXPECTED' | 'CANCELLED')} ServiceStopStatus
 */

/**
 * @typedef {('ARRIVED' | 'DEPARTED' | 'ON_TIME' | 'EXPECTED' | 'CANCELLED' |
 *  'ARRIVED_LATE' | 'DEPARTED_LATE' | 'ARRIVED_EARLY' | 'DEPARTED_EARLY')} ExtendedServiceStopStatus
 */

/**
 * A condensed version of a ServiceStop
 *
 * @typedef {Object} ServiceStopModelSummary
 * @property {ExtendedServiceStopStatus} extendedArrivalStatus
 * @property {Date|null} arrivalDateTime
 * @property {ExtendedServiceStopStatus} extendedDepartureStatus
 * @property {Date|null} departureDateTime
 */

/**
 * Return the serviceStop for the given UIC
 * For now it only considers the first item in the timetableServices array.
 *
 * @param {Object} options - The options object.
 * @param {TimetableService[]} options.timetableServices - The array of timetable services.
 * @param {string} options.uic - The UIC code of the destination station.
 * @returns {ServiceStopModel|null}
 */
export const findServiceStopFromTimetableServices = ({ timetableServices, uic }) => {
  if (!timetableServices || !timetableServices.length) {
    return null;
  }

  const [{ serviceStops }] = timetableServices;
  return findServiceStop({ serviceStops, uic });
};

/**
 * Return the serviceStop for the given UIC
 *
 * @param {Object} options - The options object.
 * @param {TimetableServiceStop[]} options.serviceStops
 * @param {string} options.uic - The UIC code of the destination station.
 * @returns {ServiceStopModel|null}
 */
export const findServiceStop = ({ serviceStops, uic }) => {
  const serviceStop = serviceStops.find(({ station: { uic: stationUic } }) => stationUic === uic);

  return serviceStop || null;
};

/**
 * Return an array of serviceStops, sorted by their stopNumber
 * For now it only considers the first item in the timetableServices array.
 *
 * @param {Object} options - The options object.
 * @param {TimetableService[]} options.timetableServices - The array of timetable services.
 * @returns {TimetableServiceStop[]|null} Returns null if there are no timetableServices
 */
export const getSortedServiceStopsFromTimetableServices = ({ timetableServices }) => {
  if (!timetableServices || !timetableServices.length) {
    return null;
  }

  const [{ model, serviceStops }] = timetableServices;

  return getSortedServiceStops({ model, serviceStops });
};

export const getSortedServiceStops = ({ model: { carrier }, serviceStops: rawServiceStops }) => {
  if (!applicableCarriers.includes(carrier)) {
    return null;
  }

  const serviceStops = [...rawServiceStops].sort(
    ({ model: { stopNumber: stopNumberA } }, { model: { stopNumber: stopNumberB } }) => stopNumberA - stopNumberB
  );

  return serviceStops;
};

/**
 * Maps a TimetableServiceStop's model to a ServiceStopModel, converting date strings to Date objects.
 *
 * @param {Object} options
 * @param {TimetableServiceStop} options.serviceStop
 * @returns {ServiceStopModel}
 */
export const getTimetableStatus = ({ serviceStop }) => {
  const {
    model: {
      arrivalStatus,
      departureStatus,
      actualArrivalDateTime: rawActualArrivalDateTime,
      actualDepartureDateTime: rawActualDepartureDateTime,
      commercialArrivalDateTime: rawCommercialArrivalDateTime,
      commercialDepartureDateTime: rawCommercialDepartureDateTime,
      expectedArrivalDateTime: rawExpectedArrivalDateTime,
      expectedDepartureDateTime: rawExpectedDepartureDateTime,
      scheduledDepartureDateTime: rawScheduledDepartureDateTime,
      scheduledArrivalDateTime: rawScheduledArrivalDateTime,
      stopNumber
    }
  } = serviceStop;

  const actualArrivalDateTime = rawActualArrivalDateTime ? new Date(rawActualArrivalDateTime) : null;
  const commercialArrivalDateTime = rawCommercialArrivalDateTime ? new Date(rawCommercialArrivalDateTime) : null;
  const expectedArrivalDateTime = rawExpectedArrivalDateTime ? new Date(rawExpectedArrivalDateTime) : null;
  const actualDepartureDateTime = rawActualDepartureDateTime ? new Date(rawActualDepartureDateTime) : null;
  const commercialDepartureDateTime = rawCommercialDepartureDateTime ? new Date(rawCommercialDepartureDateTime) : null;
  const expectedDepartureDateTime = rawExpectedDepartureDateTime ? new Date(rawExpectedDepartureDateTime) : null;
  const scheduledDepartureDateTime = rawScheduledDepartureDateTime ? new Date(rawScheduledDepartureDateTime) : null;
  const scheduledArrivalDateTime = rawScheduledArrivalDateTime ? new Date(rawScheduledArrivalDateTime) : null;

  return {
    arrivalStatus,
    departureStatus,
    actualArrivalDateTime,
    commercialArrivalDateTime,
    expectedArrivalDateTime,
    actualDepartureDateTime,
    commercialDepartureDateTime,
    expectedDepartureDateTime,
    scheduledDepartureDateTime,
    scheduledArrivalDateTime,
    stopNumber
  };
};

/**
 * @param {Object} options
 * @param {TimetableServiceStop} options.serviceStop
 * @returns {ServiceStopModelSummary}
 */
export const getTimetableStatusSummary = ({ serviceStop }) => {
  const {
    arrivalStatus,
    departureStatus,
    actualArrivalDateTime,
    actualDepartureDateTime,
    commercialArrivalDateTime,
    commercialDepartureDateTime,
    expectedArrivalDateTime,
    expectedDepartureDateTime
  } = getTimetableStatus({ serviceStop });

  let extendedArrivalStatus = extendedServiceStopStatuses[arrivalStatus] || null;
  let arrivalDateTime = commercialArrivalDateTime;

  if (arrivalStatus === serviceStopStatuses.EXPECTED) {
    arrivalDateTime = expectedArrivalDateTime;
  } else if (arrivalStatus === serviceStopStatuses.ARRIVED && actualArrivalDateTime) {
    arrivalDateTime = actualArrivalDateTime;

    if (actualArrivalDateTime.getTime() < commercialArrivalDateTime.getTime()) {
      extendedArrivalStatus = extendedServiceStopStatuses.ARRIVED_EARLY;
    } else if (actualArrivalDateTime.getTime() > commercialArrivalDateTime.getTime()) {
      extendedArrivalStatus = extendedServiceStopStatuses.ARRIVED_LATE;
    } else {
      extendedArrivalStatus = extendedServiceStopStatuses.ARRIVED_ON_TIME;
    }
  } else if (arrivalStatus === serviceStopStatuses.ARRIVED && expectedArrivalDateTime) {
    arrivalDateTime = expectedArrivalDateTime;

    if (expectedArrivalDateTime.getTime() < commercialArrivalDateTime.getTime()) {
      extendedArrivalStatus = extendedServiceStopStatuses.ARRIVED_EXPECTED_EARLY;
    } else if (expectedArrivalDateTime.getTime() > commercialArrivalDateTime.getTime()) {
      extendedArrivalStatus = extendedServiceStopStatuses.ARRIVED_EXPECTED_LATE;
    }
  }

  let extendedDepartureStatus = extendedServiceStopStatuses[departureStatus] || null;
  let departureDateTime = commercialDepartureDateTime;

  if (departureStatus === serviceStopStatuses.EXPECTED) {
    departureDateTime = expectedDepartureDateTime;
  } else if (departureStatus === serviceStopStatuses.DEPARTED && actualDepartureDateTime) {
    departureDateTime = actualDepartureDateTime;

    if (actualDepartureDateTime.getTime() < commercialDepartureDateTime.getTime()) {
      extendedDepartureStatus = extendedServiceStopStatuses.DEPARTED_EARLY;
    } else if (actualDepartureDateTime.getTime() > commercialDepartureDateTime.getTime()) {
      extendedDepartureStatus = extendedServiceStopStatuses.DEPARTED_LATE;
    } else {
      extendedDepartureStatus = extendedServiceStopStatuses.DEPARTED_ON_TIME;
    }
  } else if (departureStatus === serviceStopStatuses.DEPARTED && expectedDepartureDateTime) {
    departureDateTime = expectedDepartureDateTime;

    if (expectedDepartureDateTime.getTime() < commercialDepartureDateTime.getTime()) {
      extendedDepartureStatus = extendedServiceStopStatuses.DEPARTED_EXPECTED_EARLY;
    } else if (expectedDepartureDateTime.getTime() > commercialDepartureDateTime.getTime()) {
      extendedDepartureStatus = extendedServiceStopStatuses.DEPARTED_EXPECTED_LATE;
    }
  }

  return {
    extendedArrivalStatus,
    arrivalDateTime,
    extendedDepartureStatus,
    departureDateTime
  };
};

export const localeToLanguageKey = ({ locale }) => {
  switch (locale) {
    case locales.deDE:
      return languages.de;
    case locales.frFR:
      return languages.fr;
    case locales.nlNL:
      return languages.nl;
    case locales.enGB:
    default:
      return languages.en;
  }
};

/**
 * @param {Object} options
 * @param {string} [options.carrier] - Optional carrier code
 * @param {string} [options.destinationUic] - Destination UIC code
 * @param {string} [options.originUic] - Origin UIC code
 * @returns {boolean}
 */
export const isValidTimetableServicesQuery = ({ carrier, destinationUic, originUic }) => {
  if (isABSOrADSSearch(originUic, destinationUic)) {
    return false;
  }

  if (carrier && !applicableCarriers.includes(carrier)) {
    return false;
  }

  return true;
};
